import React from "react";
import { FaChevronDown, FaChevronUp, FaLightbulb, FaChartBar, FaGraduationCap } from "react-icons/fa";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const COLORS = ["#2148C0", "#4C6FE0", "#7B93EA", "#A7B8F2", "#3B82F6", "#60A5FA"];
const WARNING_COLOR = "#F59E0B";

const parseSubjects = (subjectsValue) => {
    if (Array.isArray(subjectsValue)) return subjectsValue;
    try {
        return JSON.parse(subjectsValue || "[]");
    } catch {
        return [];
    }
};

const PerformancePanel = ({ profile, readiness, onUseWeakSubjects, isOpen, onToggle }) => {
    const subjects = profile ? parseSubjects(profile.subjects) : [];
    const scores = readiness?.subjects || [];
    const weakSubjects = [...scores].sort((a, b) => a.score - b.score).slice(0, 3);
    const weakSet = new Set(weakSubjects.map((s) => s.subject));

    const handleUseWeakSubjects = () => {
        if (!weakSubjects.length) return;
        onUseWeakSubjects(`Help me improve in ${weakSubjects.map((s) => s.subject).join(", ")}`);
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 lg:w-80 shrink-0 lg:overflow-y-auto">
            <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <span className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                    <FaChartBar className="text-[#2148C0]" size={12} /> My Performance
                </span>
                {isOpen ? <FaChevronUp className="text-gray-400" size={12} /> : <FaChevronDown className="text-gray-400" size={12} />}
            </button>

            {isOpen && (
                <div className="p-4 space-y-4">
                    {profile && (
                        <div className="bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center gap-1.5 text-gray-400">
                                <FaGraduationCap size={11} />
                                <p className="text-xs">Class</p>
                            </div>
                            <p className="text-sm font-medium text-gray-800 mt-0.5">{profile.yearGroup || "Not set"}</p>
                            {subjects.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {subjects.slice(0, 6).map((s) => (
                                        <span key={s} className="text-[10px] bg-white border border-gray-200 text-gray-600 rounded-full px-2 py-0.5">
                                            {s}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {scores.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-4">Answer a few questions first to unlock personalized insights.</p>
                    ) : (
                        <>
                            <div>
                                <p className="text-xs text-gray-400 mb-1">Subject scores</p>
                                <ResponsiveContainer width="100%" height={180}>
                                    <BarChart data={scores} margin={{ top: 5, right: 5, left: -20, bottom: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="subject" angle={-45} textAnchor="end" height={60} fontSize={9} interval={0} />
                                        <YAxis fontSize={10} />
                                        <Tooltip formatter={(value) => [`${value}%`, "Score"]} />
                                        <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                                            {scores.map((entry, index) => (
                                                <Cell
                                                    key={entry.subject}
                                                    fill={weakSet.has(entry.subject) ? WARNING_COLOR : COLORS[index % COLORS.length]}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {weakSubjects.length > 0 && (
                                <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                                    <p className="text-xs font-medium text-amber-700 mb-2">Needs more support</p>
                                    <ul className="space-y-1.5">
                                        {weakSubjects.map((s) => (
                                            <li key={s.subject} className="flex items-center justify-between text-xs">
                                                <span className="text-gray-700">{s.subject}</span>
                                                <span className="font-semibold" style={{ color: WARNING_COLOR }}>
                                                    {s.score}%
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={handleUseWeakSubjects}
                                        className="mt-3 w-full flex items-center justify-center gap-2 bg-white border border-[#2148C0]/40 text-[#2148C0] rounded-full py-2 text-xs font-medium hover:bg-blue-50 transition"
                                    >
                                        <FaLightbulb size={11} /> Help me improve in these
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default PerformancePanel;
